import { useEffect, useState } from "react";
import type { ExecutionActivity as Activity } from "../lib/types";
import { getToken } from "../lib/api";
import { apiUrl } from "../lib/net";
import { fmtDateTime } from "../lib/format";

const names:Record<string,string>={read:"读取文件",exec:"执行命令",write:"保存资料",workboard_heartbeat:"更新执行端心跳",workboard_worker_log:"写入事件记录",workboard_block:"报告暂停",workboard_complete:"报告完成",browser:"浏览器操作"};
const actions:Record<string,string>={snapshot:"读取页面",screenshot:"截取画面",act:"执行页面操作",navigate:"打开页面",tabs:"检查标签页",open:"打开标签页",close:"关闭标签页",resize:"调整窗口"};
const seconds=(ms:number)=>`${Math.max(0,ms/1000).toFixed(1)} 秒`;
export function ExecutionActivity({runId,since,active,stoppedAt,plan,observed}:{runId:string;since:string;active:boolean;stoppedAt?:string;plan?:string;observed?:string}) {
  const [data,setData]=useState<Activity|null>(null),[error,setError]=useState(false),[now,setNow]=useState(Date.now);
  useEffect(()=>{
    let cancelled=false; let timer:ReturnType<typeof setTimeout>; let abort:AbortController;
    setData(null);setError(false);
    const poll=async()=>{
      abort=new AbortController();
      try{
        const response=await fetch(apiUrl(`/api/runs/${runId}/activity`),{headers:{Authorization:`Bearer ${getToken()??""}`},signal:abort.signal});
        if(!response.ok)throw new Error("activity");
        const result=await response.json() as Activity;
        if(!cancelled){setData(result);setError(false);}
      }catch{if(!cancelled)setError(true);}
      finally{if(!cancelled&&active)timer=setTimeout(()=>void poll(),3000);}
    };
    void poll();
    return()=>{cancelled=true;clearTimeout(timer);abort?.abort();};
  },[runId,since,active]);
  useEffect(()=>{const t=setInterval(()=>setNow(Date.now()),1000);return()=>clearInterval(t);},[]);
  const received=data?.receivedAt && Date.parse(data.receivedAt)>=Date.parse(since)-10000 ? data.receivedAt : null;
  const anchor=received??since;
  const tools=(data?.tools??[]).filter(t=>Date.parse(t.startedAt)>=Date.parse(anchor));
  const clock=!active&&stoppedAt?Date.parse(stoppedAt):now;
  const last=tools.at(-1), pending=tools.find(t=>!t.endedAt);
  const lastAt=last?.endedAt??last?.startedAt??anchor;
  const status=error?(active?"执行端进展读取失败，正在重试":"执行端进展读取失败"):!data?"正在读取执行端进展":!data.available?"执行端暂未提供工具记录":!active?"当前运行已暂停或结束":pending?`正在：${actions[pending.action??""]??names[pending.name]??"调用工具"}`:"等待下一次工具反馈；间隔原因尚未确认";
  return <span className="execution-activity" data-testid="execution-activity">
    <span className="activity-current"><i className={error?"activity-error":active?"activity-running":""}/>{status}</span>
    {observed&&<span className="activity-summary"><span>最近页面结果：</span><span translate="no">{observed}</span></span>}
    <span className="activity-summary"><span>最近计划：</span>{plan?<span translate="no">{plan}</span>:<span>尚未收到本轮下一步计划</span>}</span>
    <span className="activity-clock">本轮已过 {seconds(clock-Date.parse(anchor))} · 距工具反馈 {seconds(clock-Date.parse(lastAt))}</span>
    {received&&<span className="small muted" title={fmtDateTime(received)}>执行端会话已收到指令</span>}
    <span className="small muted">模型 → OpenClaw：工具指令 · OpenClaw → 模型：执行反馈</span>
    <span className="activity-scroll">{tools.slice().reverse().map(t=><span className="activity-line" key={t.id}>
      <span>{actions[t.action??""]??names[t.name]??"调用工具"}</span><span title={fmtDateTime(t.startedAt)}>{t.endedAt?`${t.failed?"失败":"完成"} · ${seconds(Date.parse(t.endedAt)-Date.parse(t.startedAt))}`:`已等待 ${seconds(clock-Date.parse(t.startedAt))}`}</span>
    </span>)}</span>
    {data&&<span className="small muted" title={fmtDateTime(data.checkedAt)}>工具记录约每 3 秒更新；耗时包含工具响应等待</span>}
  </span>;
}
